/**
 *
 * Helper functions for form validation
 *
 * Functions defined here should start with "validate"
 * and return a string if validation fails, and null
 * if it suceeds.
 *
 * @module
 */
import { EMAIL_REGEX, WEBSITE_URL_REGEX } from "../constants";
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from "../constants/auth";
import { assertIsNotNullOrUndefined } from "./assertOfType";
import { isNumber } from "./numbers";

type ValidationResult = string | null;

export function validatePresent(
  fieldName: string | undefined | null,
  value: string | null | undefined,
): ValidationResult {
  const strLength = value && value.trim()?.length > 0;

  if (!strLength) {
    return `${fieldName} is required.`;
  }

  return null;
}

export function validateNonZero(value: number | undefined | null): ValidationResult {
  if (!isNumber(value)) {
    return "Value must be a number.";
  }

  if (value === 0) {
    return "Value must be greater than 0.";
  }

  return null;
}

export function validateNumeric(value: string | undefined | null): ValidationResult {
  if (!value) {
    return null;
  }

  if (!isNumber(value)) {
    return "Value must be a number.";
  }

  return null;
}

export function validateLengthBetween(value: string, min: number, max?: number): ValidationResult {
  const msg = `Input must be between ${min} and ${max} characters.`;
  const strictEqMsg = `Input must be ${min} characters long.`;

  if (min > (max ?? Number.MAX_VALUE)) {
    throw new Error(`Error in length validation: max of ${max} < min of ${min}`);
  }

  if (!value) {
    return max ? msg : strictEqMsg;
  }

  if (!max && value.length !== min) {
    return strictEqMsg;
  }

  if (max && (value.length < min || value.length > max)) {
    return msg;
  }

  return null;
}

/**
 * @param value Code to be validated
 * @param countryCode Indicates which country's validation rules to follow.
 * @param required Whether field is required
 * @returns null if passed validation, user-facing error message otherwise
 */
export function validateZipCode(
  value?: string | null,
  countryCode?: string | null,
  required?: boolean,
): ValidationResult {
  const notPresent = validatePresent("Zip code", value);

  if (required && notPresent) {
    return notPresent;
  }

  // default to US zip code format
  const country = countryCode ?? "US";
  if (country === "US") {
    const notNumeric = validateNumeric(value);

    if (notNumeric) {
      return "Zip code must be a number.";
    }

    if (value && value.length !== 5) {
      return "Zip code must be 5 digits.";
    }
  } else if (country === "CA") {
    const regex = /^[A-Z]\d[A-Z][\s]?\d[A-Z]\d$/i;
    if (value && !regex.test(value)) {
      return "Invalid postal code. Use format A1A1A1 or A1A 1A1.";
    }
  }

  return null;
}

export function validateWebsite(value: string | undefined | null, required?: boolean): ValidationResult {
  const notPresent = validatePresent("Website URL", value);

  if (required && notPresent) {
    return notPresent;
  }

  if (value && !WEBSITE_URL_REGEX.test(value)) {
    return "Website URL is not valid.";
  }

  return null;
}

export function validateEmail(email: string | undefined | null, required = true): ValidationResult {
  const notPresent = validatePresent("Email address", email);

  if (notPresent) {
    return required ? notPresent : null;
  }

  assertIsNotNullOrUndefined(email);

  if (!EMAIL_REGEX.test(email)) {
    return "Email address is not valid.";
  }

  return null;
}

export const validateCommaSeparatedEmails = (emailString: string): string | null => {
  if (!emailString.trim()) {
    return null;
  }

  const invalidEmails: string[] = [];

  emailString
    .split(",")
    .map((email) => email.trim())
    .filter((email) => email.length > 0)
    .forEach((email) => {
      if (validateEmail(email)) {
        invalidEmails.push(email);
      }
    });

  if (!invalidEmails.length) {
    return null;
  }

  return invalidEmails.length === 1
    ? `Invalid email: ${invalidEmails[0]}`
    : `Invalid emails: ${invalidEmails.join(", ")}`;
};

export function validatePhone(value: string | null | undefined, required?: boolean): ValidationResult {
  const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;
  const notPresent = validatePresent("Phone number", value);

  if (required && notPresent) {
    return notPresent;
  }

  if (value && !phoneRegex.test(value)) {
    return "Invalid phone number.";
  }

  return null;
}

// The min and max values come from Cognito requirements
export function validatePassword(value: string | null, confirmation?: string | null): ValidationResult {
  const notPresent = validatePresent("Password", value);

  if (notPresent) {
    return notPresent;
  }

  if (!value || value.length < MIN_PASSWORD_LENGTH || value.length > MAX_PASSWORD_LENGTH) {
    return `Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters.`;
  }

  if (confirmation && value !== confirmation) {
    return "Passwords do not match.";
  }

  return null;
}

export function validateIsExactText(
  value: string | undefined,
  expected?: string,
  required?: boolean,
): ValidationResult {
  const notPresent = validatePresent("Input", value);

  if (required && notPresent) {
    return notPresent;
  }

  if (value && expected && value !== expected) {
    return `Input must match "${expected}" exactly.`;
  }

  return null;
}

export function validateURL(value: string): ValidationResult {
  try {
    new URL(value);
    return null;
  } catch (error) {
    return (error as Error).message;
  }
}

/**
 * Validate the Cognito-enforced rules for a client app's name
 * @param name - name of the client app to create
 * @returns an error message, or null if it passes validation
 */
export function validateClientAppName(name: string): ValidationResult {
  const notPresent = validatePresent("Name", name);
  const lengthCheck = validateLengthBetween(name, 1, 128);
  const regex = /^[a-zA-Z0-9 +=,.@-]+$/;
  if (notPresent || lengthCheck) {
    return notPresent || lengthCheck;
  }
  if (!regex.test(name)) {
    return "Invalid name - must contain only alphanumeric characters, whitespace, or these characters: + = , . @ - ";
  }
  return null;
}
